import {
  Button,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";

import FileDownloadIcon from "@mui/icons-material/FileDownload";
import DescriptionIcon from "@mui/icons-material/Description";
import { CustomDataset, CustomDatasetList, Role } from "data/models";
import { fetchCustomDatasetsLink } from "data/queries";
import { formatBytes } from "utils/commons";

import {
  GridColDef,
  GridRenderCellParams,
  GridValidRowModel,
} from "@mui/x-data-grid-premium";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import {
  BookText,
  CircleMinus,
  CircleX,
  Download,
  EllipsisVertical,
  Settings,
} from "lucide-react";
import { TooltipWrapper } from "components/permissions/permissions";
import { useState } from "react";

type Dictionary = {
  [key: string]: string;
};

const DisplayFormatMapping: Dictionary = {
  GDB: "GDB",
  GEOJSON: "GeoJSON",
  SHP: "Shapefile",
  TAB: "MapInfo TAB",
  LYNX: "LYNX",
  PUMP: "PUMP",
  GEOTIFF: "GEOTIFF",
  PSV: "Pipe-Separated Values",
};

const DisplayStateMapping: Dictionary = {
  "ALL STATES": "All States",
  AUSTRALIA: "Australia",
};

type params = {
  id: string;
  field: string;
  value: string;
};

function convertBytesToKb(kb: number) {
  const gb = kb / (1024 * 1024);
  return gb.toFixed(2) + " KB";
}

function formatDate(date: string) {
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const [year, month, day] = date.split("-");
  const monthIndex = parseInt(month) - 1;

  const formattedDate = `${parseInt(day)} ${months[monthIndex]}, ${parseInt(
    year,
  )}`;
  return formattedDate;
}
function ActionCell({ params }: { params: GridRenderCellParams }) {
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const { enqueueNotImplementedSnackbar } = useCustomSnackbars();

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedRow(null);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <IconButton
        onClick={(event) => {
          setMenuAnchorEl(event.currentTarget);
          setSelectedRow(params.row);
        }}
      >
        <EllipsisVertical />
      </IconButton>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem
          onClick={async () => {
            try {
              const response = await fetchCustomDatasetsLink(params.value);

              const a = document.createElement("a");
              a.href = response;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              window.URL.revokeObjectURL(response);
            } catch (er) {
              enqueueNotImplementedSnackbar();
              // If this happens it will show and popup
            }
          }}
        >
          <ListItemIcon>
            <Download size={20} />
          </ListItemIcon>
          <ListItemText>Download</ListItemText>
        </MenuItem>

        <Tooltip
          title={!params.row.docsLink ? "No docs for this dataset" : ""}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={() => {
                if (!params.row.docsLink) return;
                const url = params.row.docsLink.startsWith("http")
                  ? params.row.docsLink
                  : `https://${params.row.docsLink}`;
                window.open(url, "_blank");
              }}
              disabled={!selectedRow || !selectedRow.id || !params.row.docsLink}
            >
              <ListItemIcon>
                <BookText size={20} />
              </ListItemIcon>
              <ListItemText>Docs</ListItemText>
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
    </Box>
  );
}

const customColumns = (): GridColDef[] => {
  return [
    {
      flex: 1.5,
      field: "name",
      headerName: "Name",
    },

    {
      flex: 1,
      field: "region",
      headerName: "Regions",
      valueFormatter(params) {
        return DisplayStateMapping[params as string] ?? (params as string);
      },
    },
    {
      flex: 1,
      field: "format",
      headerName: "Format",
      valueFormatter(params) {
        return DisplayFormatMapping[params as string] ?? (params as string);
      },
    },
    {
      flex: 1,
      field: "datum",
      headerName: "Datum",
    },
    {
      flex: 1.2,
      field: "release",
      headerName: "Release Date",
      valueFormatter(params) {
        const [year, month] = (params as string).split("-").map(Number);
        const monthName = new Date(year, month - 1).toLocaleString("default", {
          month: "long",
        });
        return `${monthName} ${year}`;
      },
    },
    {
      flex: 1,
      field: "publishDate",
      headerName: "Publish Date",
      valueFormatter(params) {
        return formatDate(params as string);
      },
    },
    {
      flex: 1,
      field: "expiry",
      headerName: "Expiry",
      valueFormatter(params) {
        if (params) {
          const date = new Date(params as number);
          // Use local timezone methods (not toISOString which gives UTC)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return formatDate(`${year}-${month}-${day}`);
        }
        return "";
      },
    },
    {
      flex: 0.8,
      field: "version",
      headerName: "Version",

      renderCell: (params) => {
        return params.value ? (
          <Chip label={params.value} variant="filled" size="small" />
        ) : null;
      },
    },

    {
      flex: 1,
      field: "size",
      headerName: "File Size",
      valueFormatter(params) {
        return formatBytes(params as number);
      },
    },
    {
      flex: 0.5,
      field: "id",
      headerName: "",
      headerAlign: "right",
      align: "right",
      sortable: false,
      editable: false,
      hideSortIcons: true,
      disableColumnMenu: true,
      renderCell: (params) => <ActionCell params={params} />,
    },
  ];
};

const customersToRows = (
  datasetsList: CustomDatasetList,
): GridValidRowModel[] => {
  const rows = datasetsList.datasets.map((key: CustomDataset) => {
    return {
      id: key.id,
      name: key.name,
      region: key.region,
      format: key.format,
      size: key.size,
      release: key.release,
      version: key.version,
      publishDate: key.publishDate,
      expiry: key.expiry,
      licenced: key.licenced,
      datum: key.datum,
      status: key.status,
      docsLink: key.docsLink,
    };
  });
  return rows;
};

export { customColumns, customersToRows };
